import { useQuery } from '@tanstack/react-query'
import { Modal } from 'antd'
import React from 'react'
import { Account } from './ManageAccounts'
import HTTPClient from 'services/HTTPClient'
import Spinner from 'components/Spinner'
import ErrorIndicator from 'components/ErrorIndicator'
import { Bar, BarChart, Legend, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts'
import UserWiseQuizzes, { UserWiseMetrics } from './UserWiseQuizzes'


type Props = {
    users: Account[]
    onClose: () => {}
}

export type Quiz = {
    topic: string;
    completedAt: string;
    score: number;
    question_count: number;
    user: Account
}

export interface QuizMetrics {
    name: string;
    correctness: number;
    questions_correct: number;
    questions_incorrect: number;
    total_questions: number;
    quiz_count: number;
}

const getCorrectnessMetrics = (quizzes: Quiz[]) => {
    let metrics: UserWiseMetrics = {}
    let userDataLookup: { [user_id: number]: Account } = {}
    quizzes.map(q => {
        metrics[q.user.id] = metrics[q.user.id] || { total_questions: 0, total_score: 0, quiz_count: 0, quizzes: [], user: q.user }
        metrics[q.user.id].total_score += q.score
        metrics[q.user.id].total_questions += q.question_count
        metrics[q.user.id].quiz_count += 1
        metrics[q.user.id].quizzes.push(q)
        userDataLookup[q.user.id] = q.user
    })
    let data = Object.entries(metrics).map(([user_id, user_metrics]) => {
        let user: Account = userDataLookup[user_id]
        let correctness = (user_metrics.total_score / user_metrics.total_questions) * 100
        return {
            name: user.firstName || user.username,
            correctness,
            questions_correct: user_metrics.total_score,
            questions_incorrect: user_metrics.total_questions - user_metrics.total_score,
            total_questions: user_metrics.total_questions,
            quiz_count: user_metrics.quiz_count
        }
    })
    data.sort((a, b) => a.correctness - b.correctness);
    return {
        userQuizzes: metrics,
        quizMetrics: data
    };
}


const getTopTopics = (quizzes: Quiz[]) => {
    let metrics: { [topic: string]: number } = {}

    quizzes.map(q => {
        metrics[q.topic] = metrics[q.topic] || 0
        metrics[q.topic] += 1
    })
    let data = Object.entries(metrics).map(([topic, count]) => {
        return {
            topic,
            count
        }
    })
    return data.sort((a, b) => a.count - b.count)
}

const useQuizSessionMetrics = (users: Account[]) => {
    const url = `/api/tenant/quizzes`
    return useQuery(['quizsessions'], async () => {
        let body = {
            user_ids: users.map(u => u.id)
        }
        let quizzes: Quiz[] = await HTTPClient.get().postDataForJSONResult(url, body)
        return {
            ...getCorrectnessMetrics(quizzes),
            topTopics: getTopTopics(quizzes)
        }
    });
};

export default function QuizMetricsModal({ users, onClose }: Props) {

    const { data: metrics, isFetching, error, refetch } = useQuizSessionMetrics(users);

    if (error || isFetching) {
        return <div className="column mx-4" style={{ overflow: "auto" }}>
            {isFetching ? <div className="center">
                <Spinner className='mt-6' />
            </div> : null}
            {error ? <ErrorIndicator error={error} /> : null}
        </div>
    }
    let { userQuizzes, quizMetrics, topTopics } = metrics || {}
    console.log("Quiz metrics", quizMetrics)

    return (
        <Modal
            open
            onCancel={onClose}
            footer={null}
            width={"80vw"}
        >
            <div className='px-5'>
                <h3 className='is-size-3 mb-5 between'>
                    <strong>Performance</strong>
                </h3>
                <div className="columns">
                    <div className='column'>
                        <h3 className='is-size-5 mb-3'>% Correct</h3>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={quizMetrics}>
                                <Bar dataKey="correctness" label="Correct Answers" fill="#00d1b2" stackId="a" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend margin={{ top: 20 }} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className='column'>
                        <h3 className='is-size-5 mb-3'>Questions</h3>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={quizMetrics}>
                                <Bar dataKey="questions_correct" label="Correct Answers" fill="#00d1b2" stackId="a" />
                                <Bar dataKey="questions_incorrect" label="Incorrect Answers" fill="#f65868" stackId="a" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend margin={{ top: 20 }} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className='column'>
                        <h3 className='is-size-5 mb-3'>Top Topics</h3>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={topTopics}>
                                <Bar dataKey="count" fill="#5375e2" />
                                <XAxis dataKey="topic" />
                                <YAxis />
                                <Tooltip />
                                <Legend margin={{ top: 20 }} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div>
                    {userQuizzes && <UserWiseQuizzes
                        metrics={userQuizzes}
                    />}
                </div>
            </div>
        </Modal>
    )
}